/**
 * Identity used to detect duplicate files in a selection.
 * Prefers path (from react-dropzone) and falls back to name.
 * @param {{ path?: string, name?: string }} file
 * @returns {string}
 */
export function getFileIdentity(file) {
  return file?.path || file?.name || ''
}

/**
 * Appends incoming files that are not already present in existingFiles.
 * Does not mutate inputs. Only maps uniqueIncoming through mapIncoming.
 *
 * @template T
 * @template U
 * @param {T[]} existingFiles
 * @param {U[]} incomingFiles
 * @param {object} [options]
 * @param {(file: T | U) => string} [options.getIdentity]
 * @param {(file: U) => T} [options.mapIncoming]
 * @returns {T[]}
 */
export function mergeUniqueFiles(existingFiles, incomingFiles, options = {}) {
  const {
    getIdentity = getFileIdentity,
    mapIncoming = (file) => file,
  } = options

  if (!Array.isArray(existingFiles) || !Array.isArray(incomingFiles)) {
    throw new TypeError('mergeUniqueFiles expects existingFiles and incomingFiles arrays')
  }

  const seen = new Set(existingFiles.map(getIdentity).filter(Boolean))
  const uniqueIncoming = []

  for (const file of incomingFiles) {
    const identity = getIdentity(file)
    if (!identity || seen.has(identity)) continue
    seen.add(identity)
    uniqueIncoming.push(mapIncoming(file))
  }

  return [...existingFiles, ...uniqueIncoming]
}
